import {StructureUploadAreaCmp} from "../StructureUploadArea/StructureUploadAreaCmp.js";
import StructureGridCmp from "../StructureGrid/StructureGridCmp.js";
import StructureFormSelectorCmp from "../StructureFormSelector/StructureFormSelectorCmp.js";
import StructureIdSearchList from "./StructureIdSearchList.js";
import StructureIdList from "../../js/struct/StructureIdList.js";
import {addStructureIdsToUrl, getStructureIdsFromUrl} from "../../js/util/UrlUtils.js";

export class StructureSearchCmp {
    #children;
    /** @type {StructRepository} */
    #structRepository;
    /** @type {Function} */
    #onStructureIdChangedCb;
    #searchList = new StructureIdSearchList();

    /**
     * @param {StructRepository} structRepository
     */
    constructor(structRepository) {
        if(!structRepository)
            throw new Error();
        this.#children = {
            fileDropArea: new StructureUploadAreaCmp(this.#onStructureUploaded.bind(this)),
            structureGrid: new StructureGridCmp()
        }
        this.#children.structureGrid.onCardTitleClicked(this.#onStructureIdClicked.bind(this));
        this.#children.structureGrid.onDeleteClicked(this.#onDeleteClicked.bind(this));
        this.#structRepository = structRepository;
    }
    /** @param {HTMLElement} parent */
    init(parent) {
        this.#children.structureGrid.init(parent);
        const lastCard = this.#children.structureGrid.addLastCard();
        this.#children.fileDropArea.init(lastCard);
    }

    /**
     * @param {Function} cb callback to call when new structures added to the search or the existing ones deleted
     */
    onStructureIdsChanged(cb) {
        this.#onStructureIdChangedCb = cb;
    }
    #onStructureIdsChanged() {
        if(!this.#onStructureIdsChanged)
            throw new Error('Do not forget to set the callback!');
        this.#onStructureIdChangedCb(this.#searchList.uidsToSearchFor);
    }

    /** @param {StructureId} structureId */
    #onStructureIdClicked(structureId) {
        const forms = this.#searchList.getForms(structureId);
        const formSelector = new StructureFormSelectorCmp(this.#structRepository, structureId, forms);
        formSelector.onOk(this.#onFormsSelected.bind(this));
        formSelector.show();
    }

    /** @param {StructureId} form */
    async #onDeleteClicked(form){
        const ids = getStructureIdsFromUrl();
        const i = ids.indexOf(form.id);
        ids.splice(i, 1);
        addStructureIdsToUrl(ids);
        this.#searchList.deleteForm(form);
        this.#onStructureIdChangedCb(this.#searchList.uidsToSearchFor);
    }

    /**
     * @param {FileList} fileList
     * @returns {Promise<void>}
     */
    async #onStructureUploaded(fileList) {
        const setIds = new Set();
        const idsFromUrl = getStructureIdsFromUrl();
        idsFromUrl.forEach(id => setIds.add(id))
        for (const file of fileList) {
            /** @type {StructureId[]} */
            const newStructureIds = await this.#structRepository.register(file);
            const arrayToFind = [];
            for (const obj of newStructureIds){
                if (!setIds.has(obj.id)){
                    arrayToFind.push(obj)
                    setIds.add(obj.id)
                }
            }
            await this.searchByIds(new StructureIdList(arrayToFind).getParents(), arrayToFind);
        }
        addStructureIdsToUrl([...setIds])
    }

    /**
     *
     * @param {String[]} parentIds
     * @param {StructureId[]} structureIds
     * @returns {Promise<void>}
     */
    async searchByIds(parentIds, structureIds) {
        /** @type {StructureForms[]} */
        const newForms = await this.#structRepository.findForms(parentIds, structureIds);
        for (const forms of newForms) {
            this.#searchList.add(forms.representative, forms);
            this.#children.structureGrid.addStructure(forms.representative, forms);
        }
        this.#onStructureIdsChanged();
    }

    /**
     *
     * @param {StructureId} id
     * @param {StructureForms} forms
     */
    #onFormsSelected(id, forms) {
        this.#searchList.setForms(id, forms);
        this.#children.structureGrid.updateStructure(id, forms);
        this.#onStructureIdsChanged();
    }
}